import { useTranslation } from "react-i18next";
import { Company, CurrencyCode } from "@/types";
import { useGetOutstandingInvoicesForCompany, useGetOutstandingSettlementsForCompany } from "@/services/Accounting";

import Accordion from "@/components/organisms/AccordionDrawer";
import Text from "@/components/atoms/Text";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import OutstandingTable from "./OutstandingTable";
import styles from "./styles.module.css";

interface EconomyTabProps {
  company: Company;
  isActive: boolean;
}

export default function EconomyTab({ company, isActive }: EconomyTabProps) {
  const { t } = useTranslation();

  const {
    data: invoicesData,
    isFetching: isFetchingInvoices,
    isError: isErrorInvoices,
  } = useGetOutstandingInvoicesForCompany(company.id, { enabled: isActive });

  const {
    data: settlementsData,
    isFetching: isFetchingSettlements,
    isError: isErrorSettlements,
  } = useGetOutstandingSettlementsForCompany(company.id, { enabled: isActive });

  const invoicesTotal = invoicesData?.totalAmountOutstanding ?? 0;
  const invoicesCurrency = invoicesData?.currencyType ?? company.defaultCurrency;

  const settlementsTotal = settlementsData?.totalAmount ?? 0;
  const settlementsCurrency = settlementsData?.currencyType ?? company.defaultCurrency;

  return (
    <>
      <Accordion
        title={
          <SectionTitle
            label={t("commonAdmin:outstandingInvoices")}
            total={invoicesTotal}
            currency={invoicesCurrency}
          />
        }
      >
        <OutstandingTable
          rows={invoicesData?.invoices ?? []}
          getId={getInvoiceId}
          getAmount={getInvoiceAmount}
          total={invoicesTotal}
          currency={invoicesCurrency}
          isLoading={isFetchingInvoices}
          isError={isErrorInvoices}
        />
      </Accordion>

      <Accordion
        title={
          <SectionTitle
            label={t("commonAdmin:outstandingSettlements")}
            total={settlementsTotal}
            currency={settlementsCurrency}
          />
        }
      >
        <OutstandingTable
          rows={settlementsData?.settlements ?? []}
          getId={getSettlementId}
          getAmount={getSettlementAmount}
          total={settlementsTotal}
          currency={settlementsCurrency}
          isLoading={isFetchingSettlements}
          isError={isErrorSettlements}
        />
      </Accordion>
    </>
  );
}

const getInvoiceId = (invoice: { invoiceId: number }) => invoice.invoiceId;
const getInvoiceAmount = (invoice: { amountOutstanding: number }) => invoice.amountOutstanding;
const getSettlementId = (settlement: { settlementId: number }) => settlement.settlementId;
const getSettlementAmount = (settlement: { amount: number }) => settlement.amount;

const SectionTitle = ({ label, total, currency }: { label: string; total: number; currency: CurrencyCode }) => {
  return (
    <span className={styles.sectionTitle}>
      {label}
      <Text span size="sm">
        <NumberFormatter value={total} decimalScale={2} fixedDecimalScale suffix={` ${currency}`} />
      </Text>
    </span>
  );
};
